import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { TaskHistory, AssignableUser } from '@/types/tasks';
import { getTaskHistory } from '@/services/api';
import UserAvatar from '@/components/UserAvatar';

// =============================================================================
// Types
// =============================================================================

interface TaskActivityHistoryProps {
  taskId: string;
  /** Tenant users for resolving user IDs to names in change descriptions */
  users?: AssignableUser[];
}

// =============================================================================
// Helpers
// =============================================================================

const DEFAULT_VISIBLE_COUNT = 10;

function formatRelativeTimestamp(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffSeconds = Math.floor((now - then) / 1000);

  if (diffSeconds < 60) {
    return `${diffSeconds}s ago`;
  }

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  }

  const diffYears = Math.floor(diffMonths / 12);
  return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
}

// Human-readable label for field names
const FIELD_LABELS: Record<string, string> = {
  title: 'title',
  description: 'description',
  priority: 'priority',
  assigned_to_agent_id: 'agent assignment',
  assigned_to_user_id: 'assigned user',
  project_id: 'project',
  due_date: 'due date',
  due_time: 'due time',
  report_text: 'report',
  tags: 'tags',
};

// Fields where we show old → new values inline
const SHOW_VALUES_FOR = new Set(['priority', 'due_date', 'due_time']);

// Fields where we only say "updated X" (content too long)
const CONTENT_FIELDS = new Set(['description', 'report_text', 'tags']);

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return 'none';
  if (typeof val === 'string') return val || 'none';
  return String(val);
}

/**
 * Diff old_value and new_value JSONB snapshots and return a list of per-field change descriptions.
 * Falls back to the action string if no diff can be computed.
 */
function describeChanges(entry: TaskHistory, userMap: Map<number, string>): string[] {
  const { action, field_changed, old_value, new_value } = entry;

  if (action === 'created') {
    return ['created task'];
  }

  // If field_changed is explicitly set, use it directly (future-proof)
  if (field_changed) {
    const label = FIELD_LABELS[field_changed] || field_changed;
    if (SHOW_VALUES_FOR.has(field_changed)) {
      return [`changed ${label} from ${formatValue(old_value)} to ${formatValue(new_value)}`];
    }
    if (CONTENT_FIELDS.has(field_changed)) {
      return [`updated ${label}`];
    }
    return [`changed ${label}`];
  }

  // Diff old_value/new_value JSONB snapshots
  const oldObj = (typeof old_value === 'object' && old_value !== null) ? old_value as Record<string, unknown> : null;
  const newObj = (typeof new_value === 'object' && new_value !== null) ? new_value as Record<string, unknown> : null;

  if (!oldObj || !newObj) {
    return [action || 'updated'];
  }

  const changes: string[] = [];

  for (const key of Object.keys(newObj)) {
    const oldVal = oldObj[key];
    const newVal = newObj[key];

    // Skip if unchanged (compare as JSON strings for objects/arrays)
    if (JSON.stringify(oldVal) === JSON.stringify(newVal)) continue;

    const label = FIELD_LABELS[key] || key.replace(/_/g, ' ');

    if (CONTENT_FIELDS.has(key)) {
      if (oldVal === null && newVal !== null) {
        changes.push(`added ${label}`);
      } else {
        changes.push(`updated ${label}`);
      }
    } else if (SHOW_VALUES_FOR.has(key)) {
      changes.push(`changed ${label} from ${formatValue(oldVal)} to ${formatValue(newVal)}`);
    } else if (key === 'assigned_to_user_id') {
      if (newVal === null) {
        changes.push('unassigned user');
      } else {
        const name = userMap.get(Number(newVal));
        changes.push(name ? `assigned to ${name}` : `assigned to user #${newVal}`);
      }
    } else if (key === 'assigned_to_agent_id') {
      changes.push('changed agent assignment');
    } else if (key === 'project_id') {
      if (newVal === null) {
        changes.push('removed project');
      } else {
        changes.push('changed project');
      }
    } else {
      changes.push(`changed ${label}`);
    }
  }

  return changes.length > 0 ? changes : [action || 'updated'];
}

// =============================================================================
// Component
// =============================================================================

const TaskActivityHistory: React.FC<TaskActivityHistoryProps> = ({ taskId, users }) => {
  const [history, setHistory] = useState<TaskHistory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [showAll, setShowAll] = useState<boolean>(false);

  // Build a userId → full_name lookup map
  const userMap = useMemo(() => {
    const m = new Map<number, string>();
    if (users) {
      for (const u of users) {
        m.set(u.user_id, u.full_name);
      }
    }
    return m;
  }, [users]);

  const fetchHistory = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data: TaskHistory[] = await getTaskHistory(taskId);
      setHistory(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load activity history';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    void fetchHistory();
  }, [fetchHistory]);

  const visibleEntries: TaskHistory[] = showAll
    ? history
    : history.slice(0, DEFAULT_VISIBLE_COUNT);

  const hasMore: boolean = history.length > DEFAULT_VISIBLE_COUNT;

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      {/* Header */}
      <button
        type="button"
        onClick={() => setCollapsed((prev: boolean) => !prev)}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <h3 className="text-sm font-semibold text-gray-900">Activity</h3>
        <svg
          className={`h-4 w-4 text-gray-500 transition-transform ${collapsed ? '-rotate-90' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Content */}
      {!collapsed && (
        <div className="px-4 pb-4">
          {loading && (
            <div className="flex items-center justify-center py-6">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
              <span className="ml-2 text-sm text-gray-500">Loading activity...</span>
            </div>
          )}

          {!loading && error !== null && (
            <p className="py-4 text-sm text-red-600">{error}</p>
          )}

          {!loading && error === null && history.length === 0 && (
            <p className="py-4 text-sm text-gray-500">No activity yet</p>
          )}

          {!loading && error === null && history.length > 0 && (
            <>
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-3.5 top-0 bottom-0 w-px bg-gray-200" />

                <ul className="space-y-3">
                  {visibleEntries.map((entry: TaskHistory) => (
                    <li key={entry.id} className="relative flex items-start gap-3 pl-0">
                      {/* Timeline dot */}
                      <div className="relative z-10 flex-shrink-0">
                        <UserAvatar
                          user={{
                            id: entry.user_id,
                            full_name: entry.full_name ?? undefined,
                            username: entry.username ?? undefined,
                            avatar_url: entry.avatar_url,
                          }}
                          size="sm"
                        />
                      </div>

                      {/* Content */}
                      <div className="min-w-0 flex-1 pt-0.5">
                        {describeChanges(entry, userMap).map((desc, idx) => (
                          <p key={idx} className="text-sm text-gray-700">
                            {idx === 0 && (
                              <>
                                <span className="font-medium text-gray-900">
                                  {entry.full_name ?? entry.username ?? 'Unknown'}
                                </span>
                                {' '}
                              </>
                            )}
                            {desc}
                          </p>
                        ))}
                        <p className="mt-0.5 text-xs text-gray-400">
                          {formatRelativeTimestamp(entry.created_at)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Show all / Show less toggle */}
              {hasMore && (
                <button
                  type="button"
                  onClick={() => setShowAll((prev: boolean) => !prev)}
                  className="mt-3 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                >
                  {showAll
                    ? 'Show recent only'
                    : `Show all (${history.length} entries)`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskActivityHistory;
